import React, { useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { GuidelinesSection } from './GuidelinesSection';
import { TableOfContentsSection } from './TableOfContentsSection';
import { IntroductionSection } from './IntroductionSection';
import { BrandStorySection } from './BrandStorySection';
import { MissionVisionSection } from './MissionVisionSection';
import { BrandLogoSection } from './BrandLogoSection';
import { BrandFaviconSection } from './BrandFaviconSection';
import { DoDontSection } from './DoDontSection';
import { DefaultSection } from './DefaultSection';
import { BrandLogoCoverSection } from './BrandLogoCoverSection';
import { ColorCoverSection } from './ColorCoverSection';
import { BrandColorsSection } from './BrandColorsSection';
import { ColorPalettesSection } from './ColorPalettesSection';
import { TypographyCoverSection } from './TypographyCoverSection';
import { TypographyFontsSection } from './TypographyFontsSection';
import { PhotographyCoverSection } from './PhotographyCoverSection';
import { ThankYouCoverSection } from './ThankYouCoverSection';
import { TargetAudienceSection } from './TargetAudienceSection';
import { MoodboardGallerySection } from './MoodboardGallerySection';
import { ShortcodeHelper } from '../ui/ShortcodeHelper';

interface BrandSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode?: boolean;
}

export const BrandSection: React.FC<BrandSectionProps> = ({ section, isEditing, isGlobalDarkMode = false }) => {
  const { dispatch, state } = useBrand();
  const [editingElement, setEditingElement] = useState<string | null>(null);

  const updateSectionContent = (key: string, value: string) => {
    const updatedSection = {
      ...section,
      content: { ...section.content, [key]: value }
    };

    const sectionIndex = state.sections.findIndex(s => s.id === section.id);
    if (sectionIndex !== -1) {
      dispatch({
        type: 'UPDATE_SECTION',
        payload: { index: sectionIndex, section: updatedSection }
      });
    }
  };

  const updateSectionStyles = (key: string, styles: any) => {
    const updatedSection = {
      ...section,
      content: {
        ...section.content,
        [`${key}Styles`]: styles
      }
    };

    const sectionIndex = state.sections.findIndex(s => s.id === section.id);
    if (sectionIndex !== -1) {
      dispatch({
        type: 'UPDATE_SECTION',
        payload: { index: sectionIndex, section: updatedSection }
      });
    }
  };

  // Navigation function for table of contents
  const navigateToSection = (sectionId: string) => {
    if (isEditing) return; // Don't navigate in edit mode

    const targetIndex = state.sections.findIndex(s => s.id === sectionId);
    if (targetIndex !== -1) {
      dispatch({ type: 'SET_CURRENT_SECTION', payload: targetIndex });
    }
  };

  // Speculative preloading of section assets
  const prefetchSection = (sectionId: string) => {
    if (isEditing) return;
    const section = state.sections.find((s: any) => s.id === sectionId);
    if (section && section.content) {
      // Preload images found in content
      Object.values(section.content).forEach(value => {
        if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('/'))) {
          if (value.match(/\.(jpeg|jpg|gif|png|webp|svg)/i)) {
            const img = new Image();
            img.src = value;
          }
        }
      });
    }
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Generate section styles including customization and overlay
  const generateSectionStyles = () => {
    const baseStyles: any = {
      color: section.styles.textColor,
      fontFamily: section.styles.fontFamily,
      // Ensure proper positioning for overlay
      position: 'relative'
    };

    // Apply customization if it exists
    if (section.customization) {
      if (section.customization.backgroundType === 'color') {
        baseStyles.backgroundColor = section.customization.backgroundColor;
        baseStyles.backgroundImage = 'none';
      } else if (section.customization.backgroundType === 'gradient') {
        baseStyles.backgroundColor = 'transparent';
        baseStyles.backgroundImage = `linear-gradient(${section.customization.gradientAngle || 135}deg, ${section.customization.gradientFrom || '#8b5cf6'}, ${section.customization.gradientTo || '#06b6d4'})`;
      } else if (section.customization.backgroundType === 'image' && section.customization.backgroundImage) {
        baseStyles.backgroundColor = 'transparent';
        baseStyles.backgroundImage = `url(${section.customization.backgroundImage})`;
        baseStyles.backgroundPosition = section.customization.backgroundPosition;
        baseStyles.backgroundSize = section.customization.backgroundSize;
        baseStyles.backgroundRepeat = section.customization.backgroundRepeat;
      }

      // Apply overlay if enabled and opacity > 0
      if (section.customization.overlayEnabled && section.customization.overlayOpacity > 0) {
        const overlayRgba = hexToRgba(
          section.customization.overlayColor,
          section.customization.overlayOpacity / 100
        );

        if (section.customization.backgroundType === 'image' && section.customization.backgroundImage) {
          // For images, combine background image with overlay using linear-gradient
          baseStyles.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba}), url(${section.customization.backgroundImage})`;
        } else if (section.customization.backgroundType === 'gradient') {
          // For gradients, combine gradient with overlay
          const gradientValue = `linear-gradient(${section.customization.gradientAngle || 135}deg, ${section.customization.gradientFrom || '#8b5cf6'}, ${section.customization.gradientTo || '#06b6d4'})`;
          baseStyles.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba}), ${gradientValue}`;
        } else {
          // For solid colors, use a pseudo-element overlay approach
          baseStyles.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba})`;
        }
      }
    } else {
      // Fallback to original styles
      baseStyles.backgroundColor = section.styles.backgroundColor;
    }

    return baseStyles;
  };

  const renderSectionContent = () => {
    // Calculate page number - count ALL sections (including covers)
    const currentIndex = state.sections.findIndex(s => s.id === section.id);
    const pageNumber = currentIndex + 1; // Page numbers start at 1

    const commonProps = {
      section,
      isEditing,
      isGlobalDarkMode,
      editingElement,
      setEditingElement,
      updateSectionContent,
      updateSectionStyles,
      pageNumber
    };

    switch (section.id) {
      case 'guidelines':
        return <GuidelinesSection {...commonProps} />;

      case 'table-of-contents':
        return <TableOfContentsSection {...commonProps} navigateToSection={navigateToSection} prefetchSection={prefetchSection} />;

      case 'introduction':
        return <IntroductionSection {...commonProps} />;

      case 'brand-story':
        return <BrandStorySection {...commonProps} />;

      case 'mission-vision':
        return <MissionVisionSection {...commonProps} />;

      case 'target-audience':
        return <TargetAudienceSection {...commonProps} />;

      case 'moodboard-gallery':
        return <MoodboardGallerySection {...commonProps} />;

      case 'brand-logo':
        return <BrandLogoSection {...commonProps} />;

      case 'brand-favicon':
        return <BrandFaviconSection {...commonProps} />;

      case 'do-dont':
        return <DoDontSection {...commonProps} />;

      case 'brand-logo-cover':
        return <BrandLogoCoverSection {...commonProps} />;

      case 'color-cover':
        return <ColorCoverSection {...commonProps} />;

      case 'brand-colors':
        return <BrandColorsSection {...commonProps} />;

      case 'color-palettes':
        return <ColorPalettesSection {...commonProps} />;

      case 'typography-cover':
        return <TypographyCoverSection {...commonProps} />;

      case 'typography-fonts':
        return <TypographyFontsSection {...commonProps} />;

      case 'photography-cover':
        return <PhotographyCoverSection {...commonProps} />;

      case 'thank-you-cover':
        return <ThankYouCoverSection {...commonProps} />;

      default:
        return <DefaultSection {...commonProps} />;
    }
  };

  return (
    <div
      className="w-full h-screen relative"
      style={generateSectionStyles()}
    >
      {renderSectionContent()}

      {/* Show ShortcodeHelper when in edit mode */}
      {isEditing && <ShortcodeHelper isGlobalDarkMode={isGlobalDarkMode} />}
    </div>
  );
};